import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import aboutIcon from '../../assests/about.png';
import projectsIcon from '../../assests/projects.png';
import blogsIcon from '../../assests/blogs.png';
import skillsIcon from '../../assests/skills.png';
import contactIcon from '../../assests/contacts.png';

const navItems = [
  { label: 'About', href: '#about', icon: aboutIcon },
  { label: 'Projects', href: '#projects', icon: projectsIcon },
  { label: 'Blogs', href: '#blogs', icon: blogsIcon },
  { label: 'Skills', href: '#skills', icon: skillsIcon },
  { label: 'Contact', href: '#contact', icon: contactIcon },
];

export default function Navbar() {
  const [activeSection, setActiveSection] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Track active section
      const sections = navItems.map(item => item.href.slice(1));
      for (const id of [...sections].reverse()) {
        const el = document.getElementById(id);
        if (el && window.scrollY >= el.offsetTop - 120) {
          setActiveSection(id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMenuOpen(false); // Close menu first
    
    // Give the menu a tiny bit of time to start closing before scrolling
    setTimeout(() => {
      const target = document.querySelector(href);
      if (target) {
        // Fallback to simple scroll if smooth scrolling is buggy on the device
        const top = target.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 50);
  };

  return (
    <>
      {/* Desktop Navbar - Top Right Pill */}
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-6 right-6 lg:right-12 z-50 hidden md:block"
      >
        <div className="bg-[#0a0a0a]/80 backdrop-blur-md border border-white/10 rounded-full px-8 py-3 flex items-center gap-8 shadow-2xl">
          {navItems.map((item) => {
            const isActive = activeSection === item.href.slice(1);
            return (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="flex flex-col items-center justify-center gap-2 group transition-all cursor-pointer"
              >
                {/* Badge Icon Container */}
                <div className={`relative w-10 h-10 transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-110 ${isActive ? '-translate-y-1 scale-110' : ''}`}>
                  {isActive && (
                    <div className="absolute inset-0 bg-[#00B92E]/20 blur-md rounded-full" />
                  )}
                  <img 
                    src={item.icon} 
                    alt={item.label} 
                    className="relative w-full h-full object-contain"
                  />
                </div>
                {/* Label */}
                <span className={`text-[10px] uppercase tracking-wider font-semibold transition-colors duration-300 ${
                  isActive ? 'text-[#00B92E]' : 'text-white/60 group-hover:text-white'
                }`}>
                  {item.label}
                </span>
              </a>
            );
          })}
        </div>
      </motion.nav>

      {/* Mobile Navbar */}
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 md:hidden bg-black/95 backdrop-blur-md border-b border-white/10"
      >
        <div className="flex items-center justify-between h-16 px-6 relative z-20">
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); setMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex items-center gap-2 cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full border-2 border-[#00B92E] flex items-center justify-center omnitrix-glow">
              <div className="w-3 h-3 rounded-full bg-[#00B92E]" />
            </div>
            <span className="text-white font-bold text-sm tracking-wider" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              MD TOUFIQUE
            </span>
          </a>

          <button
            type="button"
            aria-label="Toggle Menu"
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-white p-2 transition-colors cursor-pointer relative z-50"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <div className={`w-6 h-[2px] bg-current mb-1.5 transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-[8px]' : ''}`} />
            <div className={`w-6 h-[2px] bg-current mb-1.5 transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
            <div className={`w-6 h-[2px] bg-current transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-[8px]' : ''}`} />
          </button>
        </div>

        {/* Mobile menu dropdown */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full left-0 right-0 bg-black/95 backdrop-blur-xl border-b border-white/10 shadow-2xl z-10"
            >
              <div className="px-6 py-8 grid grid-cols-3 gap-y-8 gap-x-4">
                {navItems.map((item) => {
                  const isActive = activeSection === item.href.slice(1);
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      onClick={(e) => handleNavClick(e, item.href)}
                      className="flex flex-col items-center gap-3 cursor-pointer"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="w-12 h-12 relative flex items-center justify-center">
                        {isActive && (
                          <div className="absolute inset-0 bg-[#00B92E]/20 blur-md rounded-full" />
                        )}
                        <img 
                          src={item.icon} 
                          alt={item.label} 
                          className={`relative w-10 h-10 object-contain transition-transform ${isActive ? 'scale-110' : ''}`} 
                        />
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider font-semibold ${
                        isActive ? 'text-[#00B92E]' : 'text-white/60'
                      }`}>
                        {item.label}
                      </span>
                    </a>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
}